import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { IMultiYearAvailableLanguage } from '@modeso/types__aumico-lib-annualreport-be';
import { HamButtonComponent } from '../../../../../../../aumico-components/src/lib';

/** Locale code → existing app-wide i18n key (reused from the user-edit form). */
const LOCALE_I18N_KEYS: Record<string, string> = {
    'de-CH': 'aumico_users_edit_user_general_information_languages_german_switzerland',
    'de-DE': 'aumico_users_edit_user_general_information_languages_german_germany',
    'en-US': 'aumico_users_edit_user_general_information_languages_english_us',
    'fr-CH': 'aumico_users_edit_user_general_information_languages_french_switzerland',
    'it-CH': 'aumico_users_edit_user_general_information_languages_italian_switzerland',
};

/**
 * Single-select language filter for the Mehrjahresvergleich controls-bar.
 *
 * Mirrors the `user-filter-drop-down` (aumico-components) pattern 1:1:
 *   - `<ham-button>` trigger with chevron-down right-icon
 *   - `[variant]="filterIsActive ? 'transparent' : 'white'"` + `[outline]="true"`
 *   - `hamFlyout` directive flyout, positioned via `getBoundingClientRect()`
 *   - Custom-styled native checkboxes (same `.custom-checkbox` rules as the
 *     user-filter SCSS, ported into the local stylesheet)
 *   - Apply (teal) + Cancel (white) action row at the bottom
 *
 * Single-select semantics: the visual is a checkbox (consistent with the
 * other selectors in the multi-year feature) but only ONE language can be
 * checked at a time — clicking another replaces the pending selection.
 * The change is staged in `pendingLanguage` and only emitted on Apply, so
 * the user can cancel without firing a re-fetch.
 *
 * Renders nothing when the company has a single locale — handled by the
 * `*ngIf="hasMultipleLanguages"` in the parent template.
 */
@Component({
    selector: 'myc-language-switch',
    templateUrl: './language-switch.component.html',
    styleUrls: ['./language-switch.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitchComponent {
    @Input() public availableLanguages: IMultiYearAvailableLanguage[] = [];
    @Input() public activeLanguage: string | null = null;

    @Output() public languageChanged = new EventEmitter<string>();

    public openedFlyout = false;
    /** Staged selection while the flyout is open. Committed via `applyFilter`. */
    public pendingLanguage: string | null = null;
    public flyoutPosition: { top: number; left: number; right: number } = { top: 0, left: 0, right: 0 };

    /** Reuses the app-wide `aumico_users_edit_user_general_information_languages_*`
     *  family so we don't maintain a parallel `aumico_locale_*` set. Unknown
     *  locales fall back to the raw code via the template's `defaultValue`. */
    public localeI18nKey(lang: string): string {
        return LOCALE_I18N_KEYS[lang] ?? '';
    }

    public isChecked(lang: string): boolean {
        return this.pendingLanguage === lang;
    }

    /**
     * Open the flyout. Position is computed off the trigger button's bounding
     * rect — same approach as `user-filter-drop-down`. The pending selection
     * is initialised to the currently active language so Cancel = no-op.
     */
    public openFlyout(buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;
        if (!buttonElement) return;
        const rect = buttonElement.getBoundingClientRect();
        this.flyoutPosition = {
            top: rect.bottom + 8,
            right: rect.right,
            left: rect.left,
        };
        this.pendingLanguage = this.activeLanguage;
        this.openedFlyout = true;
    }

    public closeFlyout(): void {
        this.openedFlyout = false;
    }

    /** Single-select: clicking any entry replaces the pending value. */
    public onSelect(lang: string): void {
        this.pendingLanguage = lang;
    }

    /** Commit the pending selection. No-op when unchanged from `activeLanguage`. */
    public applyFilter(): void {
        this.closeFlyout();
        if (this.pendingLanguage != null && this.pendingLanguage !== this.activeLanguage) {
            this.languageChanged.emit(this.pendingLanguage);
        }
    }

    /** Discard the pending selection — close without emit. */
    public cancelFilter(): void {
        this.pendingLanguage = this.activeLanguage;
        this.closeFlyout();
    }
}
